import React, { useState, useEffect, useCallback } from 'react';
import styles from './CreateProgramDirector.module.scss';
import TextField from '../../../components/textField/TextField';
import Dropdown from '../../../components/dropdown/Dropdown';
import Button from '../../../components/button/Button';
import Breadcrumbs from '../../../components/breadcrumbs/Breadcrumbs';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import ImageUploader from '../../../components/imageUploader/ImageUploader';
import { useNavigate, useParams } from 'react-router-dom';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import { formatPhoneNumber, getInitials } from '../../../utils/stringUtils';

const CreateProgramDirector = () => {
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const { programDirectorId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [subscriptionOptions, setSubscriptionOptions] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [profilePicture, setProfilePicture] = useState({ initials: '', image: null });
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    subscriptionId: null,
    isActive: true,
  });
  const [readOnlyFields, setReadOnlyFields] = useState({ email: '', subscriptionName: '' });

  useEffect(() => {
    setProfilePicture(prev => ({
      ...prev,
      initials: getInitials(formData.firstName, formData.lastName),
    }));
  }, [formData.firstName, formData.lastName]);

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        const response = await axiosClient.get('/subscription', { params: { limit: 100, page: 1 } });
        setSubscriptionOptions(
          (response.data?.subscriptions || []).map(s => ({ value: s.id, label: s.name }))
        );
      } catch (error) {
        showToast(error.message, 'error');
      }
    };
    fetchSubscriptions();
  }, [showToast]);

  const fetchProgramDirector = useCallback(async () => {
    if (!programDirectorId) return;
    setIsLoading(true);
    try {
      const response = await axiosClient.get(
        `/users/program-director-management/program-directors/${programDirectorId}`
      );
      const pd = response.data?.programDirector || response.data || response;
      const fetched = {
        firstName: pd.firstName || '',
        lastName: pd.lastName || '',
        email: pd.email || '',
        mobileNumber: pd.phone ? formatPhoneNumber(pd.phone) : (pd.mobileNumber ? formatPhoneNumber(pd.mobileNumber) : ''),
        subscriptionId: pd.subscriptions?.[0]?.id ?? null,
        isActive: pd.isActive ?? true,
      };
      setFormData(fetched);
      setProfilePicture({
        initials: getInitials(pd.firstName, pd.lastName),
        image: pd.profilePicture || null,
      });
      setReadOnlyFields({
        email: pd.email || '',
        subscriptionName: pd.subscriptions?.[0]?.name || '—',
      });
    } catch (error) {
      showToast(error.message || 'Failed to fetch program director', 'error');
      navigate('/program-directors');
    } finally {
      setIsLoading(false);
    }
  }, [programDirectorId, showToast, navigate]);

  useEffect(() => {
    fetchProgramDirector();
  }, [fetchProgramDirector]);

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Last name is required';
    if (!programDirectorId) {
      if (!formData.email?.trim()) newErrors.email = 'Email is required';
      if (!formData.subscriptionId) newErrors.subscriptionId = 'Subscription is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const buildPayload = (isEdit) => {
    const payload = new FormData();
    payload.append('firstName', formData.firstName);
    payload.append('lastName', formData.lastName);
    payload.append('mobileNumber', formData.mobileNumber.replace(/\D/g, ''));
    payload.append('isActive', formData.isActive);
    if (!isEdit) {
      payload.append('email', formData.email);
      payload.append('subscriptionId', formData.subscriptionId);
    }
    if (imageFile) payload.append('profilePicture', imageFile);
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSaving(true);
    try {
      const headers = { 'Content-Type': 'multipart/form-data' };
      if (programDirectorId) {
        await axiosClient.patch(
          `/users/program-director-management/program-directors/${programDirectorId}`,
          buildPayload(true),
          { headers }
        );
        showToast('Program director updated successfully');
      } else {
        await axiosClient.post('/program-director/create', buildPayload(false), { headers });
        showToast('Program director created successfully');
      }
      navigate('/program-directors');
    } catch (error) {
      showToast(error.message || 'Failed to save program director', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {(isLoading || isSaving) && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Program Directors', path: '/program-directors' },
            { label: programDirectorId ? 'Edit Program Director' : 'Add Program Director' },
          ]}
        />
      </div>
      <div className={styles.container}>
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <h2>Program Director Details</h2>

          <ImageUploader
            {...profilePicture}
            onChange={(file) => setImageFile(file)}
          />

          <div className={styles.row}>
            <div className={styles.column50}>
              <TextField
                label="First Name *"
                value={formData.firstName}
                onChange={(val) => handleChange('firstName', val)}
                placeholder="Enter first name"
                error={errors.firstName}
              />
            </div>
            <div className={styles.column50}>
              <TextField
                label="Last Name *"
                value={formData.lastName}
                onChange={(val) => handleChange('lastName', val)}
                placeholder="Enter last name"
                error={errors.lastName}
              />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.column50}>
              {programDirectorId ? (
                <TextField
                  label="Email"
                  value={readOnlyFields.email}
                  onChange={() => {}}
                  disabled
                />
              ) : (
                <TextField
                  label="Email *"
                  value={formData.email}
                  onChange={(val) => handleChange('email', val)}
                  placeholder="Enter email"
                  error={errors.email}
                />
              )}
            </div>
            <div className={styles.column50}>
              <TextField
                label="Mobile Number"
                value={formData.mobileNumber}
                onChange={(val) => handleChange('mobileNumber', formatPhoneNumber(val))}
                placeholder="000-000-0000"
              />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.column50}>
              {programDirectorId ? (
                <TextField
                  label="Subscription"
                  value={readOnlyFields.subscriptionName}
                  onChange={() => {}}
                  disabled
                />
              ) : (
                <Dropdown
                  label="Subscription *"
                  value={formData.subscriptionId}
                  onChange={(val) => handleChange('subscriptionId', val)}
                  options={subscriptionOptions}
                  error={errors.subscriptionId}
                  searchable
                />
              )}
            </div>
            <div className={styles.column50}>
              <Dropdown
                label="Status"
                value={formData.isActive ? 'true' : 'false'}
                onChange={(val) => handleChange('isActive', val === 'true')}
                options={[
                  { value: 'true', label: 'Active' },
                  { value: 'false', label: 'Inactive' },
                ]}
              />
            </div>
          </div>

          <div className={styles.actions}>
            <Button
              label="Cancel"
              variant="white"
              size="medium"
              onClick={() => navigate('/program-directors')}
            />
            <Button
              label={programDirectorId ? 'Save Changes' : 'Add Program Director'}
              variant="primary"
              size="medium"
              type="submit"
            />
          </div>
        </form>
      </div>
    </>
  );
};

export default CreateProgramDirector;
